import React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import OfferBadgeLabel from 'components/Common/OfferBadgeLabel/OfferBadgeLabel'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineMapMarkerIcon from 'components/Luxkit/Icons/line/LineMapMarkerIcon'
import MarkdownRender from 'components/Luxkit/MarkdownRender'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import TextLink from 'components/Luxkit/TextLink'
import { OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import OfferDescription from '../OfferDescription'
import OfferPromotions from '../OfferPromotions'
import { mediaQueryUp } from 'components/utils/breakpoint'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Image from 'components/Common/Image'
import OfferRating from 'components/Common/NumberRating/OfferRating'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import Heading from 'components/Luxkit/Typography/Heading'

const OfferIntroSection = styled(BodyText)`
  grid-row: 4;

  ${mediaQueryUp.tablet} {
    grid-row: 3;
    grid-column: 1;
  }

  > * + * {
    margin-top: ${rem(16)};
  }
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${mediaQueryUp.tablet} {
    grid-row: 2;
    grid-column: span 2;
  }

  ${mediaQueryUp.desktop} {
    grid-column: 1;
  }
`

const PropertyLogo = styled(Image)`
  display: none;

  ${mediaQueryUp.desktop} {
    margin-left: ${rem(16)};
    display: block;
    height: ${rem(75)};
  }
`

interface Props {
  offer: App.HotelOffer;
  promotions: Array<OfferPackageOptionView>;
}

function AlwaysOnPropertyDetails(props: Props) {
  const {
    offer,
    promotions,
  } = props

  const location = [offer.locationHeading, offer.locationSubheading].filter(t => t).join(', ')

  return (<>
    <Header>
      <VerticalSpacer gap={8}>
        <BodyTextBlock
          variant="small"
          data-testid="offer-location"
          startIcon={<LineMapMarkerIcon />}
        >
          {location} • <TextLink href="#getting-there">View on map</TextLink>
        </BodyTextBlock>
        <Heading variant="heading2" as="h1">{offer.property.name}</Heading>
        <BodyText variant="large" as="h2">{offer.name}</BodyText>

        {isOfferRatingDisplayable(offer.property.rating) && <OfferRating
          link
          inlineLabel
          variant="small"
          rating={offer.property.rating}
        />}
      </VerticalSpacer>
      {offer.property.logoImageId &&
        <PropertyLogo
          dpr={2}
          height={75}
          enhancedFormats={false}
          id={offer.property.logoImageId}
          alt={`${offer.property.name || 'Vendor'} logo`}
        />}
    </Header>
    {offer.badge && <OfferBadgeLabel badge={offer.badge} />}

    <OfferIntroSection variant="large">
      {offer.description && <OfferDescription description={offer.description} />}
      {offer.additionalDescription && <MarkdownRender type="compact" fontSize="medium" content={offer.additionalDescription} />}
      {promotions.length > 0 && <OfferPromotions offer={offer} promotions={promotions} />}
    </OfferIntroSection>
  </>)
}

export default React.memo(AlwaysOnPropertyDetails)
